import { useEffect, useState } from 'react';
import { Card, EmptyState, Input, Select, Spinner, Badge } from '@adop/ui';
import { ArrowDown, ArrowUp, Database, Eye, Search } from 'lucide-react';
import { fetchAdminTable, fetchAdminTables } from '../../lib/admin-api';
import { useAsync } from '../../lib/useAsync';
import { useDebouncedValue } from '../../lib/use-debounced-value';
import { clampPage, formatAdminCell, toggleSort, type AdminSortState } from '../../lib/admin-table';
import { Alert } from '../../components/Alert';
import { PageHeader } from '../../components/PageHeader';
import { LoadingBlock } from '../../components/LoadingState';
import { AdminPagination } from './AdminPagination';

const PAGE_SIZES = [25, 50, 100];

export function AdminDataPage() {
  const tablesState = useAsync(fetchAdminTables, []);

  const [selected, setSelected] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0] ?? 25);
  const [search, setSearch] = useState('');
  const [sortState, setSortState] = useState<AdminSortState>({ desc: false });
  const debouncedSearch = useDebouncedValue(search, 300);

  // Default to the first allow-listed table once the list loads.
  useEffect(() => {
    if (!selected && tablesState.data && tablesState.data.tables.length > 0) {
      setSelected(tablesState.data.tables[0]?.name ?? null);
    }
  }, [selected, tablesState.data]);

  // New table, search or page size restarts pagination and clears sort.
  useEffect(() => {
    setPage(1);
    setSortState({ desc: false });
  }, [selected, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const tableState = useAsync(
    () =>
      selected
        ? fetchAdminTable(selected, {
            page,
            page_size: pageSize,
            search: debouncedSearch.trim() || undefined,
            sort: sortState.sort,
            desc: sortState.desc,
          })
        : Promise.resolve(null),
    [selected, page, pageSize, debouncedSearch, sortState.sort, sortState.desc],
  );

  const clickSort = (column: string) => {
    setSortState((current) => toggleSort(current, column));
    setPage(1);
  };

  const data = tableState.data;
  const safePage = data ? clampPage(page, data.total, pageSize) : page;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Data"
        description="Read-only browser over the platform's allow-listed tables."
      />

      <Alert variant="info">
        <span className="inline-flex items-center gap-1.5">
          <Eye className="h-4 w-4 shrink-0" /> Read-only. There is no way to edit a row from this
          browser.
        </span>
      </Alert>

      {tablesState.error ? <Alert>{tablesState.error}</Alert> : null}

      <div className="grid gap-5 lg:grid-cols-[16rem_1fr]">
        <Card className="h-fit">
          <div className="border-b border-zinc-200 px-4 py-3">
            <p className="text-sm font-semibold text-zinc-900">Tables</p>
          </div>
          {tablesState.loading ? (
            <LoadingBlock label="Loading tables…" />
          ) : (
            <ul className="max-h-[28rem] overflow-y-auto p-2">
              {(tablesState.data?.tables ?? []).map((table) => {
                const active = table.name === selected;
                return (
                  <li key={table.name}>
                    <button
                      type="button"
                      onClick={() => setSelected(table.name)}
                      className={`flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm ${
                        active ? 'bg-zinc-900 text-white' : 'text-zinc-700 hover:bg-zinc-100'
                      }`}
                    >
                      <span className="truncate font-medium">{table.name}</span>
                      <span className={`text-xs ${active ? 'text-zinc-300' : 'text-zinc-400'}`}>
                        ~{table.approx_rows}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <div className="min-w-0 space-y-4">
          {tableState.error ? <Alert>{tableState.error}</Alert> : null}

          {!selected ? (
            <EmptyState icon={<Database className="h-6 w-6" />} title="Choose a table" />
          ) : (
            <Card className="overflow-hidden">
              <div className="flex flex-wrap items-end gap-3 border-b border-zinc-200 p-4">
                <div className="min-w-[12rem] flex-1">
                  <label className="mb-1 block text-xs font-medium text-zinc-500" htmlFor="admin-data-search">
                    Search
                  </label>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                    <Input
                      id="admin-data-search"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Filter rows"
                      className="pl-9"
                    />
                  </div>
                </div>
                <div className="w-32">
                  <Select
                    label="Page size"
                    value={String(pageSize)}
                    onChange={(event) => setPageSize(Number(event.target.value))}
                  >
                    {PAGE_SIZES.map((size) => (
                      <option key={size} value={size}>
                        {size} rows
                      </option>
                    ))}
                  </Select>
                </div>
                <span className="ml-auto self-center">
                  <Badge variant="outline">
                    <Database className="mr-1 h-3.5 w-3.5" /> {selected}
                  </Badge>
                </span>
              </div>

              {tableState.loading && !data ? (
                <div className="flex items-center justify-center py-12">
                  <Spinner />
                </div>
              ) : !data || data.rows.length === 0 ? (
                <EmptyState
                  icon={<Database className="h-6 w-6" />}
                  title={debouncedSearch.trim() ? 'No matching rows' : 'No rows'}
                />
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead className="border-b border-zinc-200 bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
                        <tr>
                          {data.columns.map((column) => {
                            const active = sortState.sort === column;
                            return (
                              <th key={column} className="whitespace-nowrap px-4 py-2 font-medium">
                                <button
                                  type="button"
                                  onClick={() => clickSort(column)}
                                  className="inline-flex items-center gap-1 hover:text-zinc-900"
                                >
                                  {column}
                                  {active ? (
                                    sortState.desc ? (
                                      <ArrowDown className="h-3.5 w-3.5" />
                                    ) : (
                                      <ArrowUp className="h-3.5 w-3.5" />
                                    )
                                  ) : null}
                                </button>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100">
                        {data.rows.map((row, rowIndex) => (
                          <tr key={rowIndex} className="hover:bg-zinc-50">
                            {data.columns.map((column, columnIndex) => (
                              <td
                                key={column}
                                className="max-w-[24rem] truncate whitespace-nowrap px-4 py-2 text-zinc-700"
                                title={formatAdminCell(row[columnIndex])}
                              >
                                {formatAdminCell(row[columnIndex])}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <AdminPagination
                    page={safePage}
                    pageSize={pageSize}
                    total={data.total}
                    onPageChange={setPage}
                    unit="rows"
                  />
                </>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
